import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
  appendConsentLog,
  getCookiePreferences,
  hasCookieBannerBeenResolved,
  setCookiePreferences,
} from '../lib/consentStorage';
import { CURRENT_COOKIE_POLICY_VERSION, CURRENT_PRIVACY_VERSION, CURRENT_TERMS_VERSION } from '../lib/legalVersions';
import { fetchClientIp } from '../lib/fetchClientIp';

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(() => !hasCookieBannerBeenResolved());
  const [manageOpen, setManageOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  const logConsent = async (event: 'cookie_preferences') => {
    const ip = await fetchClientIp();
    appendConsentLog({
      terms_version: CURRENT_TERMS_VERSION,
      privacy_version: CURRENT_PRIVACY_VERSION,
      cookie_policy_version: CURRENT_COOKIE_POLICY_VERSION,
      ip_address: ip,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      event,
    });
  };

  const acceptAll = async () => {
    setCookiePreferences({
      essential: true,
      analytics: true,
      version: CURRENT_COOKIE_POLICY_VERSION,
    });
    await logConsent('cookie_preferences');
    setVisible(false);
    setManageOpen(false);
  };

  const rejectNonEssential = async () => {
    setCookiePreferences({
      essential: true,
      analytics: false,
      version: CURRENT_COOKIE_POLICY_VERSION,
    });
    await logConsent('cookie_preferences');
    setVisible(false);
    setManageOpen(false);
  };

  const saveManage = async () => {
    setCookiePreferences({
      essential: true,
      analytics,
      version: CURRENT_COOKIE_POLICY_VERSION,
    });
    await logConsent('cookie_preferences');
    setVisible(false);
    setManageOpen(false);
  };

  React.useEffect(() => {
    const existing = getCookiePreferences();
    if (existing) setAnalytics(existing.analytics);
  }, [manageOpen]);

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6"
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
          >
            <div className="container mx-auto max-w-4xl rounded-2xl border border-border/60 bg-background/95 backdrop-blur-xl shadow-2xl p-4 sm:p-6">
              <p className="text-sm text-muted-foreground mb-4">
                We use cookies and similar technologies to improve your experience, analyze usage, and enhance performance.
                By clicking &quot;Accept All,&quot; you agree to our use of cookies. You can manage preferences in settings.
              </p>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setManageOpen(true)}>
                  Manage Preferences
                </Button>
                <Button variant="secondary" size="sm" onClick={rejectNonEssential}>
                  Reject Non-Essential
                </Button>
                <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600" onClick={acceptAll}>
                  Accept All
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Essential cookies are always on. Toggle analytics cookies to opt in or out of usage analytics.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-4 py-2">
            <div>
              <Label>Analytics &amp; performance</Label>
              <p className="text-xs text-muted-foreground">Helps us understand product usage.</p>
            </div>
            <Switch checked={analytics} onCheckedChange={setAnalytics} />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setManageOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveManage}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
